import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config/config';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import '../css/MessageManagement.css';


const formatDate = (dateString) => {
    // 값이 없으면 1970-01-01 같은 가짜 날짜 대신 "—" 를 보인다.
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const MessageModal = ({ message, onClose }) => {
    if (!message) return null;
    const isScheduled = Boolean(message.scheduled_date);

    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div className="sds-modal messages-page__modal" role="dialog" aria-modal="true" aria-labelledby="message-detail-title" onClick={(e) => e.stopPropagation()}>
                <div className="sds-modal__head">
                    <h2 id="message-detail-title" className="sds-modal__title">
                        {isScheduled ? '예약 메시지 상세' : '일반 메시지 상세'}
                    </h2>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={onClose} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
                <div className="sds-modal__body">
                    <dl className="messages-page__kv">
                        <dt>유형</dt><dd>{message.message_type || '—'}</dd>
                        <dt>발송자</dt><dd>{message.sender || '—'}</dd>
                        <dt>수신자</dt><dd>{message.recipient || '—'}</dd>
                        {isScheduled
                            ? (<><dt>예약일시</dt><dd>{formatDate(message.scheduled_date)}</dd></>)
                            : (<><dt>발송일</dt><dd>{formatDate(message.sent_date)}</dd></>)}
                        <dt>제목</dt><dd>{message.title || '—'}</dd>
                    </dl>
                    <h3 className="messages-page__content-label">내용</h3>
                    <p className="messages-page__content">{message.content || '—'}</p>
                </div>
                <div className="sds-modal__foot">
                    <button className="sds-btn sds-btn--secondary" onClick={onClose}>닫기</button>
                </div>
            </div>
        </div>
    );
};

const SendMessageModal = React.memo(({ showSendModal, setShowSendModal, isLoading, activeTab }) => {
    const [formData, setFormData] = useState({
        userSearchTerm: '',
        messageTitle: '',
        messageType: '',
        messageContent: '',
        isScheduled: activeTab === 'scheduled',
        scheduledDate: '',
        scheduledTime: ''
    });
    
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const messageTitleRef = useRef(null);
    const messageTypeRef = useRef(null);
    const messageContentRef = useRef(null);
    const navigate = useNavigate();

    const handleInputChange = (field) => (e) => {
        setFormData({...formData, [field]: e.target.value});
    };

    const handleSearchUsers = async () => {
        try {
            if (!formData.userSearchTerm.trim()) {
                alert('검색어를 입력해주세요.');
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                console.error('인증 토큰이 없습니다.');
                navigate('/');
                return;
            }

            const hospitalId = localStorage.getItem('hospital_id');
            const regionCode = localStorage.getItem('region_code');
            const districtCode = localStorage.getItem('district_code');

            if (!hospitalId || !regionCode || !districtCode) {
                console.error('관리자 정보가 없습니다:', { hospitalId, regionCode, districtCode });
                alert('관리자 정보가 올바르지 않습니다. 다시 로그인해주세요.');
                navigate('/');
                return;
            }

            const params = {
                searchTerm: formData.userSearchTerm.trim(),
                hospital_id: hospitalId,
                region_code: regionCode,
                district_code: districtCode
            };

            console.log('검색 요청 파라미터:', params);

            const response = await axios.get(`${API_BASE_URL}/api/app-users/search`, {
                params: {
                    searchTerm: formData.userSearchTerm.trim(),
                    hospital_id: hospitalId,
                    region_code: regionCode,
                    district_code: districtCode
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.data.success) {
                if (response.data.users.length === 0) {
                    alert('검색 결과가 없습니다.');
                }
                setSearchedUsers(response.data.users);
                setShowSearchResults(true);
            } else {
                console.error('사용자 검색 실패:', response.data.message);
                alert(response.data.message || '사용자 검색에 실패했습니다.');
                setSearchedUsers([]);
            }
        } catch (error) {
            console.error('사용자 검색 실패:', error);
            if (error.response?.data) {
                console.error('서버 응답:', error.response.data);
                if (error.response.status === 401 || error.response.status === 403) {
                    alert('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.clear();
                    navigate('/');
                    return;
                }
                alert(error.response.data.message || '사용자 검색에 실패했습니다.');
            } else {
                alert('서버와의 통신 중 오류가 발생했습니다.');
            }
            setSearchedUsers([]);
            setShowSearchResults(false);
        }
    };

    const handleUserSelect = async (user) => {
        try {
            if (!selectedUsers.find(u => u.user_id === user.user_id)) {
                setSelectedUsers([...selectedUsers, user]);
                setSearchedUsers([]);
                setShowSearchResults(false);
                setFormData({...formData, userSearchTerm: ''});
            }
        } catch (error) {
            console.error('사용자 선택 실패:', error);
        }
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    };

    // 시간과 분 옵션 생성
    const hourOptions = useMemo(() => {
        return Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    }, []);

    const minuteOptions = useMemo(() => {
        return Array.from({ length: 6 }, (_, i) => (i * 10).toString().padStart(2, '0'));
    }, []);

    // 시간 상태 분리
    const [timeState, setTimeState] = useState({
        hour: '',
        minute: ''
    });

    // 시간 변경 핸들러
    const handleTimeChange = (type) => (e) => {
        const newTimeState = { ...timeState, [type]: e.target.value };
        setTimeState(newTimeState);
        
        // 시간과 분이 모두 선택된 경우에만 formData 업데이트
        if (newTimeState.hour !== '' && newTimeState.minute !== '') {
            setFormData({
                ...formData,
                scheduledTime: `${newTimeState.hour}:${newTimeState.minute}`
            });
        }
    };

    // 메시지 전송 처리 함수
    const handleSendMessage = async () => {
        try {
            const currentUser = {
                id: localStorage.getItem('id'),
                name: localStorage.getItem('user_name')
            };

            const recipientString = selectedUsers.map(user => user.name).join(', ');

            const messageData = {
                message_type: formData.messageType,
                title: formData.messageTitle,
                content: formData.messageContent,
                sender: currentUser.name,
                recipient: recipientString,
                created_id: parseInt(currentUser.id),
                created_at: new Date().toISOString()
            };

            // 예약 메시지인 경우
            if (activeTab === 'scheduled') {
                // 필수 입력값 검증
                if (!formData.scheduledDate || !formData.scheduledTime) {
                    alert('예약 날짜와 시간을 모두 선택해주세요.');
                    return;
                }

                // 예약 시간이 현재 시간보다 이전인지 확인
                const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
                if (scheduledDateTime <= new Date()) {
                    alert('예약 시간은 현재 시간 이후로 설정해주세요.');
                    return;
                }

                // 예약 메시지 전송 API 호출
                const response = await axios.post(`${API_BASE_URL}/api/messages/schedule`, {
                    ...messageData,
                    scheduled_date: `${formData.scheduledDate}T${formData.scheduledTime}:00`,
                    sent_date: null
                });

                console.log('예약 메시지 응답:', response.data); // 응답 확인용 로그

                if (response.data.success) {
                    alert(`예약 메시지가 성공적으로 설정되었습니다.\n예약 시간: ${formData.scheduledDate} ${formData.scheduledTime}`);
                    setShowSendModal(false);                    
                }
            } 
            // 일반 메시지인 경우
            else {
                // 일반 메시지 전송 API 호출
                const response = await axios.post(`${API_BASE_URL}/api/messages/send`, {
                    ...messageData,
                    scheduled_date: null,
                    sent_date: new Date().toISOString()
                });

                // 응답 구조에 상관없이 성공 시 alert 표시
                alert("메시지가 성공적으로 전송되었습니다.");
                setShowSendModal(false);
                // 메시지 목록 새로고침
                // refreshMessages();
            }
        } catch (error) {
            console.error('메시지 전송 중 오류 발생:', error);
            if (error.response?.data?.errorMessage === 'Reservations can only be made at least 10 minutes later.') {
                alert('SMS 예약 발송 설정 실패: 예약 시간은 현재 시간으로부터 최소 10분 이후로 설정해주세요.');
            } else if (error.response?.data?.message) {
                alert(error.response.data.message);
            } else {
                alert('메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
            }
        }
    };

    const scheduled = activeTab === 'scheduled';

    return (
        <div className="sds-modal-overlay">
            <div className="sds-modal messages-page__modal" role="dialog" aria-modal="true" aria-labelledby="message-send-title">
                <div className="sds-modal__head">
                    <h2 id="message-send-title" className="sds-modal__title">{formData.isScheduled ? '예약 메시지 발송' : '일반 메시지 발송'}</h2>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={() => setShowSendModal(false)} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>

                <div className="sds-modal__body messages-page__form">
                    <section className="messages-page__section" aria-labelledby="message-recipients-title">
                        <h3 id="message-recipients-title" className="messages-page__section-title">수신자</h3>
                        <div className="messages-page__search">
                            <div className="messages-page__row">
                                <div className="sds-input-group messages-page__grow">
                                    <Search size={16} aria-hidden="true" />
                                    <input
                                        className="sds-input"
                                        placeholder="이름으로 사용자 검색"
                                        aria-label="사용자 검색"
                                        value={formData.userSearchTerm}
                                        onChange={(e) => {
                                            setFormData({...formData, userSearchTerm: e.target.value});
                                            if (e.target.value === '') {
                                                setShowSearchResults(false);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSearchUsers();
                                            }
                                        }}
                                        onBlur={() => {
                                            setTimeout(() => {
                                                setShowSearchResults(false);
                                            }, 200);
                                        }}
                                        onFocus={() => {
                                            if (searchedUsers.length > 0) {
                                                setShowSearchResults(true);
                                            }
                                        }}
                                    />
                                </div>
                                <button className="sds-btn sds-btn--secondary" onClick={handleSearchUsers}>검색</button>
                            </div>
                            {showSearchResults && searchedUsers.length > 0 && (
                                <ul className="messages-page__results" aria-label="검색 결과">
                                    {searchedUsers.map(user => (
                                        <li key={user.id} className="messages-page__result">
                                            <span>{user.name}</span>
                                            <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => handleUserSelect(user)}>추가</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="messages-page__chips" aria-label="선택된 수신자">
                            {selectedUsers.length === 0 ? (
                                <span className="messages-page__hint">검색해서 수신자를 추가해 주세요.</span>
                            ) : selectedUsers.map(user => (
                                <span key={user.id} className="messages-page__chip">
                                    {user.name}
                                    <button className="messages-page__chip-remove" onClick={() => handleRemoveUser(user.id)} aria-label={`${user.name} 빼기`}>
                                        <X size={16} aria-hidden="true" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </section>

                    <section className="messages-page__section" aria-labelledby="message-body-title">
                        <h3 id="message-body-title" className="messages-page__section-title">내용</h3>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="message-title">제목</label>
                            <input
                                id="message-title"
                                ref={messageTitleRef}
                                className="sds-input"
                                placeholder="제목을 입력해주세요"
                                value={formData.messageTitle}
                                onChange={handleInputChange('messageTitle')}
                            />
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="message-type">구분</label>
                            <select
                                id="message-type"
                                ref={messageTypeRef}
                                className="sds-select"
                                value={formData.messageType}
                                onChange={handleInputChange('messageType')}
                            >
                                <option value="">메시지 구분 선택</option>
                                <option value="공지">공지</option>
                                <option value="알림">알림</option>
                                <option value="경고">경고</option>
                            </select>
                        </div>
                        <div className="sds-field">
                            <label className="sds-label" htmlFor="message-content">내용</label>
                            <textarea
                                id="message-content"
                                ref={messageContentRef}
                                className="sds-textarea messages-page__textarea"
                                placeholder="메시지 내용을 입력해주세요"
                                value={formData.messageContent}
                                onChange={handleInputChange('messageContent')}
                            />
                        </div>
                    </section>

                    {scheduled && (
                        <section className="messages-page__section" aria-labelledby="message-schedule-title">
                            <h3 id="message-schedule-title" className="messages-page__section-title">예약</h3>
                            <div className="messages-page__schedule">
                                <div className="sds-field">
                                    <label className="sds-label" htmlFor="message-schedule-date">예약 날짜</label>
                                    <input
                                        id="message-schedule-date"
                                        className="sds-input"
                                        type="date"
                                        value={formData.scheduledDate}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            scheduledDate: e.target.value
                                        })}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                                <div className="sds-field">
                                    <span className="sds-label" id="message-schedule-time-label">예약 시간</span>
                                    <div className="messages-page__row" role="group" aria-labelledby="message-schedule-time-label">
                                        <select value={timeState.hour} onChange={handleTimeChange('hour')} className="sds-select" aria-label="시">
                                            <option value="">시</option>
                                            {hourOptions.map((hour) => (
                                                <option key={hour} value={hour}>{hour}시</option>
                                            ))}
                                        </select>
                                        <select value={timeState.minute} onChange={handleTimeChange('minute')} className="sds-select" aria-label="분">
                                            <option value="">분</option>
                                            {minuteOptions.map((minute) => (
                                                <option key={minute} value={minute}>{minute}분</option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="messages-page__hint">10분 단위로 고를 수 있어요. 지금부터 10분 이후만 예약됩니다.</p>
                                </div>
                            </div>
                        </section>
                    )}
                </div>

                <div className="sds-modal__foot">
                    <button className="sds-btn sds-btn--secondary" onClick={() => setShowSendModal(false)}>취소</button>
                    <button className="sds-btn sds-btn--primary" onClick={handleSendMessage} disabled={isLoading}>
                        {scheduled ? '예약' : '발송'}
                    </button>
                </div>
            </div>
        </div>
    );
});

export default function MessageManagement() {
    const [messages, setMessages] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTypes, setSelectedTypes] = useState(new Set(["전체"]));
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const messagesPerPage = 5;
    const [userInfo, setUserInfo] = useState({
        userName: localStorage.getItem('user_name') || '알 수 없음',
        userRole: localStorage.getItem('user_role') || '권한 없음',
        userId: localStorage.getItem('id') || '',
        affiliation: '',
        management_group: ''
    });
    const [showSendModal, setShowSendModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('general');
    const [filterType, setFilterType] = useState('all');

    // 필터 변경 이벤트 감지
    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    const filteredMessages = useMemo(() => {
        return messages.filter(message => {
            if (activeTab === 'general') {
                return message.scheduled_date === null;
            } else {
                return message.scheduled_date !== null;
            }
        });
    }, [messages, activeTab]);

    const indexOfLastMessage = currentPage * messagesPerPage;
    const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
    const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
    const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

    // 페이지 그룹 계산 로직
    const pagesPerGroup = 5;
    const currentGroup = Math.ceil(currentPage / pagesPerGroup) || 1;
    const startPage = (currentGroup - 1) * pagesPerGroup + 1;
    const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);

    // 탭, 필터 변경 시 1페이지로 리셋
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, filterType]);

    useEffect(() => {
        const initializeData = async () => {
            if (isLoading) return;
            setIsLoading(true);

            try {
                const token = localStorage.getItem('token');
                
                if (!token) {
                    alert('로그인이 필요합니다.');
                    navigate('/');
                    return;
                }

                try {
                    const decodedToken = jwtDecode(token);
                    const currentTime = Date.now() / 1000;
                    
                    if (decodedToken.exp < currentTime) {
                        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                        localStorage.clear();
                        navigate('/');
                        return;
                    }
                } catch (error) {
                    console.error('토큰 검증 실패:', error);
                    alert('인증에 실패했습니다. 다시 로그인해주세요.');
                    localStorage.clear();
                    navigate('/');
                    return;
                }

                const userName = localStorage.getItem('user_name');
                const userRole = localStorage.getItem('user_role');
                const userId = localStorage.getItem('id');
                const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

                if (userId) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                    
                    const response = await axios.get(`${API_BASE_URL}/api/users/${userId}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });

                    if (response.data.success) {
                        const userData = response.data.data;

                        localStorage.setItem('user_name', userData.name);
                        localStorage.setItem('user_role', userData.role);
                        localStorage.setItem('user_affiliation', userData.affiliation);
                        localStorage.setItem('user_management_group', userData.management_group);

                        setUserInfo({
                            userName: userData.name || userName || '알 수 없음',
                            userRole: userData.role || userRole || '권한 없음', 
                            userId: userData.id || userId || '',
                            affiliation: userData.affiliation || localStorage.getItem('user_affiliation') || '',
                            management_group: userData.management_group || localStorage.getItem('user_management_group') || ''
                        });
                    }
                }

                await new Promise(resolve => setTimeout(resolve, 50));
                
                // API 요청 파라미터 설정
                let params = {};
                
                if (isSuperAdmin) {
                    // 슈퍼 관리자는 is_super_admin 파라미터만 전송
                    params = { is_super_admin: 'true' };
                } else {
                    // 일반 관리자의 경우에만 관리자 정보 검증 및 필터 적용
                    const hospitalId = localStorage.getItem('hospital_id');
                    const regionCode = localStorage.getItem('region_code');
                    const districtCode = localStorage.getItem('district_code');

                    if (!hospitalId || !regionCode || !districtCode) {
                        console.error('관리자 정보가 없습니다. 로컬 스토리지 확인:', localStorage);
                        return;
                    }

                    if (filterType === 'all') {
                        params = {
                            hospital_id: hospitalId,
                            region_code: regionCode,
                            district_code: districtCode
                        };
                    } else if (filterType === 'region') {
                        params = {
                            region_code: regionCode,
                            district_code: districtCode
                        };
                    } else if (filterType === 'hospital') {
                        params = {
                            hospital_id: hospitalId,
                            region_code: regionCode,
                            district_code: districtCode
                        };
                    }
                }

                console.log('API 요청 파라미터:', params);

                const messagesResponse = await axios.get(`${API_BASE_URL}/api/getmessages`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    params: params
                });

                if (messagesResponse.data && messagesResponse.data.success) {
                    setMessages(messagesResponse.data.data || []);
                } else {
                    console.error('메시지 조회 실패:', messagesResponse.data);
                    setMessages([]);
                }

            } catch (error) {
                console.error('데이터 초기화 실패:', error);
                if (error.response) {
                    console.error('서버 응답:', error.response.data);
                    if (error.response.status === 401) {
                        alert('인증이 만료되었습니다. 다시 로그인해주세요.');
                        localStorage.clear();
                        navigate('/');
                    } else {
                        alert('메시지 조회 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
                    }
                } else {
                    alert('서버와의 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
                }
                setMessages([]);
            } finally {
                setIsLoading(false);
            }
        };

        initializeData();
    }, [selectedTypes, searchTerm, currentPage, navigate, filterType]);

    const handleMessageClick = (message) => {
        setSelectedMessage(message);
    };

    const handleCloseModal = () => {
        setSelectedMessage(null);
    };

    const handleSendMessageClick = () => {
        setShowSendModal(true);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const pageNumbers = Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, i) => startPage + i);
    const scheduledTab = activeTab === 'scheduled';

    return (
        <div className="messages-page">
            <NavigationBar />
            <main className="sds-page">
                <div className="sds-page-header">
                    <h1 className="sds-page-header__title">메시지 목록</h1>
                    <div className="sds-page-header__actions">
                        <button className="sds-btn sds-btn--primary" onClick={handleSendMessageClick} disabled={isLoading}>
                            메시지 발송
                        </button>
                    </div>
                </div>

                <div className="sds-tabs messages-page__tabs" role="tablist" aria-label="메시지 종류">
                    <button role="tab" aria-selected={!scheduledTab} className={`sds-tabs__item${!scheduledTab ? ' is-active' : ''}`} onClick={() => setActiveTab('general')}>
                        일반메시지
                    </button>
                    <button role="tab" aria-selected={scheduledTab} className={`sds-tabs__item${scheduledTab ? ' is-active' : ''}`} onClick={() => setActiveTab('scheduled')}>
                        예약메시지
                    </button>
                </div>

                <section className="messages-page__card">
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th className="sds-table__num">번호</th>
                                    <th>유형</th>
                                    <th>제목</th>
                                    <th>{scheduledTab ? '예약일시' : '발송일'}</th>
                                    <th>발송자</th>
                                    <th>수신자</th>
                                    {scheduledTab && <th className="sds-table__center">상태</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {currentMessages.length > 0 ? (
                                    currentMessages.map((message, index) => (
                                        <tr key={index} className="messages-page__row-clickable" onClick={() => handleMessageClick(message)}>
                                            <td className="sds-table__num">{message.id}</td>
                                            <td>{message.message_type}</td>
                                            <td className="messages-page__title-cell">{message.title}</td>
                                            <td className="sds-table__muted">{formatDate(scheduledTab ? message.scheduled_date : message.sent_date)}</td>
                                            <td>{message.sender}</td>
                                            <td className="messages-page__recipient-cell">{message.recipient}</td>
                                            {scheduledTab && (
                                                <td className="sds-table__center"><span className="sds-badge sds-badge--info">예약됨</span></td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={scheduledTab ? 7 : 6} className="sds-empty">
                                            {scheduledTab ? '예약된 메시지가 없습니다.' : '발송된 메시지가 없습니다.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 0 && (
                        <div className="sds-pagination">
                            <button className="sds-pagination__btn" onClick={handlePreviousPage} disabled={currentPage === 1} aria-label="이전 페이지">
                                <ChevronLeft size={16} aria-hidden="true" />
                            </button>
                            {startPage > 1 && (
                                <>
                                    <button className="sds-pagination__btn" onClick={() => setCurrentPage(1)}>1</button>
                                    <span className="sds-pagination__ellipsis">…</span>
                                </>
                            )}
                            {pageNumbers.map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                                    aria-current={currentPage === page ? 'page' : undefined}
                                >
                                    {page}
                                </button>
                            ))}
                            {endPage < totalPages && (
                                <>
                                    <span className="sds-pagination__ellipsis">…</span>
                                    <button className="sds-pagination__btn" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                                </>
                            )}
                            <button className="sds-pagination__btn" onClick={handleNextPage} disabled={currentPage >= totalPages} aria-label="다음 페이지">
                                <ChevronRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </section>
            </main>

            {selectedMessage && <MessageModal message={selectedMessage} onClose={handleCloseModal} />}
            {showSendModal && (
                <SendMessageModal
                    showSendModal={showSendModal}
                    setShowSendModal={setShowSendModal}
                    isLoading={isLoading}
                    activeTab={activeTab}
                />
            )}
        </div>
    );
}
